import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, ArrowRight, Eye, EyeOff } from "lucide-react";

const CORRECT_CODE = "23273";

export default function PasswordGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const [showCode, setShowCode] = useState(false);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await new Promise((r) => setTimeout(r, 400));
    if (code === CORRECT_CODE) {
      localStorage.setItem("aerobic_proposal_access", "true");
      onUnlock();
    } else {
      setError(true);
      setCode("");
      setLoading(false);
      setTimeout(() => setError(false), 2500);
    }
  };

  return (
    <div className="min-h-screen bg-[#111827] flex items-center justify-center p-6 relative overflow-hidden">
      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(#38BDF8 1px, transparent 1px), linear-gradient(to right, #38BDF8 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />
      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#38BDF8]/5 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative w-full max-w-sm"
      >
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-0.5 mb-2">
            <span className="font-['Outfit'] text-2xl font-700 text-white tracking-tight">
              artificial
            </span>
            <span className="font-['Outfit'] text-2xl font-700 text-[#38BDF8] tracking-tight">
              mente
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#38BDF8] ml-0.5 mt-1.5" />
          </div>
          <p className="text-gray-500 text-sm">
            Propuesta · <span className="text-gray-400 font-medium">AEROBIC</span>
          </p>
        </div>

        {/* Card */}
        <div className="bg-[#1F2937] border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Lock icon */}
          <div className="flex items-center justify-center w-14 h-14 bg-[#38BDF8]/10 border border-[#38BDF8]/20 rounded-2xl mx-auto mb-6">
            <Lock size={24} className="text-[#38BDF8]" />
          </div>

          <h2 className="font-['Outfit'] text-xl font-600 text-white text-center mb-1">
            Documento confidencial
          </h2>
          <p className="text-gray-500 text-sm text-center mb-7">
            Ingresá el código de acceso para ver la propuesta.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Input */}
            <div className="relative">
              <input
                data-testid="password-gate-input"
                type={showCode ? "text" : "password"}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Código de acceso"
                maxLength={10}
                className={`w-full bg-white/5 border ${
                  error ? "border-red-500/60" : "border-white/15"
                } text-white placeholder-gray-600 rounded-xl px-4 py-3 pr-11 text-base focus:outline-none focus:border-[#38BDF8]/60 transition-colors`}
              />
              <button
                type="button"
                onClick={() => setShowCode(!showCode)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-400 transition-colors"
              >
                {showCode ? <EyeOff size={17} /> : <Eye size={17} />}
              </button>
            </div>

            {/* Error message */}
            <AnimatePresence>
              {error && (
                <motion.p
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="text-red-400 text-xs text-center"
                >
                  Código incorrecto. Verificá e intentá nuevamente.
                </motion.p>
              )}
            </AnimatePresence>

            {/* Submit */}
            <button
              data-testid="password-gate-submit"
              type="submit"
              disabled={!code || loading}
              className="w-full flex items-center justify-center gap-2 bg-[#38BDF8] hover:bg-[#0EA5E9] disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 rounded-xl transition-colors text-sm"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Verificando...
                </span>
              ) : (
                <>
                  Acceder a la propuesta
                  <ArrowRight size={16} />
                </>
              )}
            </button>
          </form>
        </div>

        <p className="text-center text-gray-700 text-xs mt-6">
          © {new Date().getFullYear()} artificialmente · Propuesta confidencial
        </p>
      </motion.div>
    </div>
  );
}
